import { getSeedanceTaskList } from '../api/seedance';
import { useSeedanceTaskStore } from '../store/seedanceTaskStore';
import { getTaskListRecentRangeUnixSeconds, normalizeStatus } from './seedanceTaskUtils';

const PAGE_SIZE = 100;

/** 若后端在列表接口返回全局成功/完成数量，从这里读取（字段名按常见约定尝试）。 */
export function pickSucceededAggregateFromPayload(data: unknown): number | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  const keys = [
    'succeeded_total',
    'success_total',
    'completed_total',
    'finish_total',
    'success_count',
    'completed_count',
    'finished_count',
    'succeed_count',
  ];
  for (const k of keys) {
    const n = Number(d[k]);
    if (Number.isFinite(n) && n >= 0) return Math.floor(n);
  }
  const st = d.stats ?? d.statistics;
  if (st && typeof st === 'object') {
    const o = st as Record<string, unknown>;
    const n = Number(o.succeeded ?? o.success ?? o.completed ?? o.finished);
    if (Number.isFinite(n) && n >= 0) return Math.floor(n);
  }
  return null;
}

function listItemSucceeded(item: any): boolean {
  const innerData = item?.data && typeof item.data === 'object' ? item.data : {};
  return normalizeStatus(item.status || innerData.status) === 'succeeded';
}

/**
 * 分页拉取并统计「已完成」数量（接口未返回聚合字段时使用）。
 * @param skipFirstPageItems 当前已取得的第一页 items 时可传入，少发一次请求（须确为第 1 页数据）。
 */
export async function countSucceededAcrossAllPages(options: {
  totalHint: number;
  onDebug?: (message: string, isError?: boolean) => void;
  skipFirstPageItems?: any[];
}): Promise<number> {
  const { totalHint, onDebug, skipFirstPageItems } = options;
  if (totalHint <= 0) return 0;

  let succeeded = 0;
  let page = 1;
  let seen = 0;
  let serverTotal = totalHint;

  if (skipFirstPageItems?.length) {
    for (const item of skipFirstPageItems) {
      if (listItemSucceeded(item)) succeeded++;
    }
    seen = skipFirstPageItems.length;
    page = 2;
  }

  const { startTimestamp, endTimestamp } = getTaskListRecentRangeUnixSeconds();
  while (seen < serverTotal && page < 120) {
    const data: any = await getSeedanceTaskList({
      apiKey: '',
      page,
      pageSize: PAGE_SIZE,
      onDebug,
      taskId: '',
      startTimestamp,
      endTimestamp,
    });
    const items: any[] = Array.isArray(data?.data?.items) ? data.data.items : [];
    const t = Number(data?.data?.total);
    if (Number.isFinite(t) && t >= 0) serverTotal = t;
    for (const item of items) {
      if (listItemSucceeded(item)) succeeded++;
    }
    seen += items.length;
    if (items.length === 0) break;
    page++;
  }

  return succeeded;
}

let succeededRecomputeTimer: ReturnType<typeof setTimeout> | null = null;

export function cancelGlobalSucceededRecompute(): void {
  if (!succeededRecomputeTimer) return;
  clearTimeout(succeededRecomputeTimer);
  succeededRecomputeTimer = null;
}

export function scheduleGlobalSucceededRecompute(options: {
  totalHint: number;
  onDebug?: (message: string, isError?: boolean) => void;
  firstPageItems?: any[];
  firstPageWasPageOne: boolean;
}): void {
  if (succeededRecomputeTimer) clearTimeout(succeededRecomputeTimer);
  succeededRecomputeTimer = setTimeout(() => {
    succeededRecomputeTimer = null;
    const { totalHint, onDebug, firstPageItems, firstPageWasPageOne } = options;
    void countSucceededAcrossAllPages({
      totalHint,
      onDebug,
      skipFirstPageItems: firstPageWasPageOne ? firstPageItems : undefined,
    }).then((n) => {
      useSeedanceTaskStore.getState().setTaskSucceededTotal(n);
    });
  }, 350);
}
